import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { StartupProfile } from '../types/firestore';
import { useTracking } from '../utils/useTracking';
import {
  BUSINESS_MODEL_DISPLAY_MAP,
  STAGE_DISPLAY_MAP,
  CURRENCY_DISPLAY_MAP,
  getDisplayName,
} from '../constants/display-mappings';
import { formatSeekingInvestment } from '../utils/formatCompactAmount';
import { startupIndustryLabels } from '../utils/startupIndustryLabels';
import locationIcon from '../assets/icons/location.png';
import industryIcon from '../assets/icons/industry.png';
import bizModelIcon from '../assets/icons/biz_model.gif';
import stageIcon from '../assets/icons/stage.gif';
import amountIcon from '../assets/icons/amount.gif';
import landingImage from '../assets/landingimage.png';

interface FeaturedStartupProps {
  startup: StartupProfile;
  onViewStartup: () => void;
  sectionTitle?: string;
}

const FeaturedStartup: React.FC<FeaturedStartupProps> = ({
  startup,
  onViewStartup,
  sectionTitle = 'Featured Startup',
}) => {
  const { trackInteraction } = useTracking();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [showBelow, setShowBelow] = useState(false);

  const industryLabels = startupIndustryLabels(startup);

  const hasTooltipData = Boolean(
    startup.country ||
      industryLabels.length > 0 ||
      startup.businessModel ||
      startup.stage ||
      startup.investment
  );

  const formatAmount = () => {
    if (!startup.investment) return 'N/A';
    const currency = startup.currency
      ? getDisplayName(CURRENCY_DISPLAY_MAP, startup.currency)
      : 'EUR';
    return formatSeekingInvestment(startup.investment, currency);
  };

  useEffect(() => {
    if (!isHovered || !cardRef.current || !hasTooltipData) return;

    const updatePosition = () => {
      if (!cardRef.current) return;
      const rect = cardRef.current.getBoundingClientRect();
      const needsFlip = rect.top < 70 + 160;
      setShowBelow(needsFlip);
      setTooltipPosition({
        top: needsFlip ? rect.bottom + 12 : rect.top - 12,
        left: rect.left + rect.width / 2,
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isHovered, hasTooltipData]);
  const getbgSize = () => {
    if (typeof window === 'undefined') return '130%';
    if (window.matchMedia('(max-width: 639px)').matches) return 'cover';
    if (window.matchMedia('(min-width: 1800px)').matches) return 'cover';
    return '130%';
  };
  const [bgSize, setBgSize] = React.useState(getbgSize);
  React.useEffect(() => {
    const mqMobile = window.matchMedia('(max-width: 639px)');
    const mqWide = window.matchMedia('(min-width: 1800px)');
    const handler = () => setBgSize(getbgSize());
    mqMobile.addEventListener('change', handler);
    mqWide.addEventListener('change', handler);
    return () => {
      mqMobile.removeEventListener('change', handler);
      mqWide.removeEventListener('change', handler);
    };
  }, []);

  const handleViewClick = async () => {
    if (startup.id) {
      try {
        await trackInteraction({
          action: 'view',
          startupId: startup.id,
          source: 'catalog',
          category: 'Featured',
          metadata: {
            elementClicked: 'featured-startup',
            sessionId: `session-${Date.now()}`,
          },
        });
      } catch (error) {
        console.error('Error tracking featured startup click:', error);
      }
    }
    onViewStartup();
  };

  return (
    <div className="relative w-full overflow-hidden min-h-[220px] sm:min-h-[460px] bg-[#262626]">
      {/* Landing image: golden field + unicorn on the right — position so unicorn (right third) is visible */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-no-repeat"
          style={{
            backgroundImage: `url(${landingImage})`,
            backgroundPosition: 'right bottom',
            backgroundSize: bgSize,
            filter: 'saturate(0.78) brightness(0.32) contrast(1.02)',
          }}
        />
        {/* Single overlay: dark at top + left, clear on right for unicorn */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 50%), linear-gradient(to right, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.28) 45%, transparent 54%)',
          }}
        />
      </div>

      {/* Content over left side only */}
      <div className="relative z-10 container mx-auto flex flex-col px-4 lg:px-6 pt-10 md:pt-32 pb-8">
        <div className="flex flex-col sm:flex-row items-start gap-5 sm:gap-6">
          {/* Pitch Deck Column */}
          <div className="flex flex-col items-start">
            <h2 className="text-base sm:text-xl font-bold text-white mb-2 ml-1 drop-shadow-md">
              {sectionTitle}
            </h2>
            {/* Pitch Deck card: 16:9 style to match original mockup */}
            <div
              ref={cardRef}
              data-featured-startup-card
              className="flex-shrink-0 relative group cursor-pointer transition-transform hover:scale-[1.02]"
              onClick={handleViewClick}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <div className="w-44 h-24 sm:w-64 sm:h-36 rounded-xl overflow-hidden shadow-2xl border border-white/20 relative">
                <div
                  className="absolute inset-0 flex items-center justify-center"
                  style={{
                    background: `
                    radial-gradient(ellipse 80% 80% at 20% 20%, #06b6d4 0%, transparent 50%),
                    radial-gradient(ellipse 70% 70% at 80% 80%, #92d050 0%, transparent 45%),
                    linear-gradient(135deg, #0ea5e9 0%, #06b6d4 25%, #92d050 55%, #70af2c 100%)
                  `,
                  }}
                >
                  {startup.thumbnailUrl ? (
                    <img src={startup.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                  ) : null}
                </div>

                {/* Bottom gradient and text overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Startup name, country, View profile */}
          <div className="flex flex-col justify-center flex-1 min-w-0 pt-2 text-center sm:text-left">
            <h3 className="text-xl sm:text-3xl font-bold text-white mb-1 drop-shadow-md">
              {startup.companyName}
            </h3>
            {startup.country && (
              <p className="text-white/80 font-medium text-xs sm:text-base mb-3 drop-shadow-sm">
                {startup.country}
              </p>
            )}
            <button
              type="button"
              onClick={handleViewClick}
              className="text-emerald-400 hover:text-emerald-300 font-medium text-sm underline underline-offset-4 self-center sm:self-start transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-400/50 rounded drop-shadow-sm"
            >
              View profile
            </button>
          </div>
        </div>
      </div>

      {hasTooltipData &&
        isHovered &&
        createPortal(
          <div
            className="fixed opacity-100 pointer-events-none transition-opacity duration-300 z-[9999]"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              transform: showBelow ? 'translate(-50%, 0%)' : 'translate(-50%, -100%)',
            }}
          >
            <div className="bg-[#3f3f3f] rounded-lg p-3 shadow-2xl min-w-[220px] border border-gray-700">
              {showBelow ? (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-[-5px]">
                  <div className="w-3 h-3 bg-[#3f3f3f] border-l border-t border-gray-700 transform rotate-45"></div>
                </div>
              ) : (
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                  <div className="w-3 h-3 bg-[#3f3f3f] border-r border-b border-gray-700 transform rotate-45"></div>
                </div>
              )}
              <div className="space-y-2.5 relative z-10">
                {startup.country && (
                  <div className="flex items-center gap-2.5 text-white text-sm">
                    <img src={locationIcon} alt="Location" className="w-4 h-4 flex-shrink-0" />
                    <span className="whitespace-nowrap">{startup.country}</span>
                  </div>
                )}
                {industryLabels.length > 0 && (
                  <div className="flex items-start gap-2.5 text-white text-sm">
                    <img
                      src={industryIcon}
                      alt="Industry"
                      className="mt-0.5 h-4 w-4 flex-shrink-0"
                    />
                    <div className="flex flex-col gap-0.5">
                      {industryLabels.map(label => (
                        <span key={label} className="whitespace-nowrap">
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {startup.businessModel && (
                  <div className="flex items-center gap-2.5 text-white text-sm">
                    <img
                      src={bizModelIcon}
                      alt="Business Model"
                      className="w-4 h-4 flex-shrink-0"
                    />
                    <span className="whitespace-nowrap">
                      {getDisplayName(BUSINESS_MODEL_DISPLAY_MAP, startup.businessModel)}
                    </span>
                  </div>
                )}
                {startup.stage && (
                  <div className="flex items-center gap-2.5 text-white text-sm">
                    <img src={stageIcon} alt="Stage" className="w-4 h-4 flex-shrink-0" />
                    <span className="whitespace-nowrap">
                      {getDisplayName(STAGE_DISPLAY_MAP, startup.stage)}
                    </span>
                  </div>
                )}
                {startup.investment && (
                  <div className="flex items-center gap-2.5 text-white text-sm">
                    <img src={amountIcon} alt="Amount" className="w-4 h-4 flex-shrink-0" />
                    <span className="whitespace-nowrap">{formatAmount()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Bottom fade — blends into the page background */}
      <div
        className="absolute bottom-0 left-0 right-0 h-6 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, transparent, #262626)' }}
      />
    </div>
  );
};

export default FeaturedStartup;
